/**
 * When a workflow starts: the trigger's kind, and the settings that kind
 * reads (`WorkflowTrigger`). Changing the kind starts its settings afresh,
 * as the server drops any a kind does not read.
 */
import type { ReactNode } from 'react';
import {
  Card, Chip, Group, MultiSelect, SegmentedControl, Select, Stack, Text, TextInput, ThemeIcon,
} from '@mantine/core';
import { IconBolt } from '@tabler/icons-react';
import { usePipelines } from '../../api/crm';
import { useProperties } from '../../api/properties';
import { useCampaigns, useEmailTemplates } from '../../api/queries';
import { useScoringSettings } from '../../api/scoring';
import { useTemplateLinks, type EmailEventKind, type RecordType, type TriggerKind, type WorkflowTrigger } from '../../api/workflows';
import { useVocab } from '../../vocab';
import {
  blankTrigger, EMAIL_EVENTS, eventKind, linkClickSentence, linkLabel, recordTypeOf, recordWord, TRIGGER_ORDER,
  triggerLabel, triggerRecordType,
} from './workflowModel';

const GROUP_ORDER: RecordType[] = ['submission', 'person', 'deal', 'task'];

export function TriggerCard({
  trigger, onChange, extra,
}: {
  trigger: WorkflowTrigger;
  onChange: (t: WorkflowTrigger) => void;
  /** Under the settings: for an enrolment, who it goes to and when (SendToSegment). */
  extra?: ReactNode;
}) {
  const v = useVocab();
  const groups = [
    ...GROUP_ORDER.map((rt) => ({
      group: `${recordWord(v, rt).replace(/^./, (x) => x.toUpperCase())}`,
      items: TRIGGER_ORDER.filter((k) => eventKind(k) && recordTypeOf(k) === rt).map((k) => ({
        value: k, label: triggerLabel(v, k),
      })),
    })),
    { group: 'Enrolled', items: [{ value: 'enrolment', label: triggerLabel(v, 'enrolment') }] },
    { group: 'Coming soon', items: [{ value: 'form_submitted', label: triggerLabel(v, 'form_submitted'), disabled: true }] },
  ];
  const set = (patch: Partial<WorkflowTrigger>) => onChange({ ...trigger, ...patch });

  return (
    <Card padding="sm" withBorder data-testid="trigger-card">
      <Stack gap="sm">
        <Group gap="xs" wrap="nowrap">
          <ThemeIcon variant="light" radius="xl" size={28} color="chartAmber"><IconBolt size={15} /></ThemeIcon>
          <Text size="sm" fw={600}>When</Text>
        </Group>
        <Select
          size="sm"
          aria-label="Trigger"
          data={groups}
          value={trigger.kind}
          allowDeselect={false}
          maxDropdownHeight={360}
          onChange={(k) => {
            // A new kind reads different settings: start them afresh.
            if (k && k !== trigger.kind) onChange(blankTrigger(k as TriggerKind));
          }}
          data-testid="trigger-kind"
        />
        <TriggerSettings trigger={trigger} set={set} />
        {extra}
        <Text size="xs" c="dimmed">
          {`Runs for each ${recordWord(v, triggerRecordType(trigger))} it starts on, at most 5 times a day for the same one.`}
        </Text>
      </Stack>
    </Card>
  );
}

/**
 * An event a "wait until" or a goal listens for: a trigger kind that
 * happens (not an enrolment) and its settings, as the trigger card has them.
 */
export function EventPicker({
  event, onChange, label = 'Event', testId,
}: { event: WorkflowTrigger; onChange: (t: WorkflowTrigger) => void; label?: string; testId?: string }) {
  const v = useVocab();
  const groups = GROUP_ORDER.map((rt) => ({
    group: `${recordWord(v, rt).replace(/^./, (x) => x.toUpperCase())}`,
    items: TRIGGER_ORDER.filter((k) => eventKind(k) && recordTypeOf(k) === rt).map((k) => ({
      value: k, label: triggerLabel(v, k),
    })),
  }));
  return (
    <Stack gap="xs">
      <Select
        size="xs"
        label={label}
        data={groups}
        value={event.kind}
        allowDeselect={false}
        maxDropdownHeight={320}
        onChange={(k) => { if (k && k !== event.kind) onChange(blankTrigger(k as TriggerKind)); }}
        data-testid={testId}
      />
      <TriggerSettings trigger={event} set={(p) => onChange({ ...event, ...p })} />
    </Stack>
  );
}

export function TriggerSettings({ trigger, set }: { trigger: WorkflowTrigger; set: (p: Partial<WorkflowTrigger>) => void }) {
  const v = useVocab();
  switch (trigger.kind) {
    case 'submission_arrived': return <StreamsSetting trigger={trigger} set={set} />;
    case 'lead_returned': return <StreamsSetting trigger={trigger} set={set} />;
    case 'person_property_changed': return <PropertySetting trigger={trigger} set={set} />;
    case 'deal_created':
    case 'deal_won':
    case 'deal_lost':
    case 'deal_stage_changed':
      return <DealSetting trigger={trigger} set={set} />;
    case 'score_band_changed': return <BandSetting trigger={trigger} set={set} />;
    case 'email_event':
      return (
        <Stack gap={4}>
          <Text size="xs" fw={500}>Which events</Text>
          <Chip.Group
            multiple
            value={trigger.email_events ?? []}
            onChange={(x) => set({ email_events: x.length ? (x as EmailEventKind[]) : null })}
          >
            <Group gap={6}>
              {EMAIL_EVENTS.map((e) => <Chip key={e.value} value={e.value} size="xs">{e.label}</Chip>)}
            </Group>
          </Chip.Group>
          <Text size="xs" c="dimmed">
            {`None chosen means all four. To narrow it to one ${v.one('campaign')}, add a condition on campaign_id.`}
          </Text>
        </Stack>
      );
    case 'email_link_clicked': return <LinkClickSetting trigger={trigger} set={set} />;
    case 'enrolment':
      return (
        <Stack gap={4}>
          <SegmentedControl
            size="xs"
            data={[{ value: 'person', label: v.Many('person') }, { value: 'submission', label: v.Many('lead') }]}
            value={trigger.record_type === 'submission' ? 'submission' : 'person'}
            onChange={(x) => set({ record_type: x as RecordType })}
            style={{ alignSelf: 'flex-start' }}
            data-testid="trigger-enrol-type"
          />
          <Text size="xs" c="dimmed">
            {`Nothing starts it by itself. Choose a segment below: now, once at a set time, or on a schedule. Segments beside the list can enrol into it too.`}
          </Text>
        </Stack>
      );
    default:
      return null;
  }
}

/**
 * A link clicked in an email: from which template (and, from that
 * template's named links, which link), or an address that contains some
 * words, for links without a name.
 */
function LinkClickSetting({ trigger, set }: { trigger: WorkflowTrigger; set: (p: Partial<WorkflowTrigger>) => void }) {
  const templates = useEmailTemplates().data?.templates ?? [];
  const links = useTemplateLinks(trigger.email_template_id ?? null).data?.links ?? [];
  const named = links.filter((l, i) => l.name && links.findIndex((x) => x.name === l.name) === i);
  const template = templates.find((t) => t.id === trigger.email_template_id) ?? null;
  const linkData = named.map((l) => ({ value: l.name!, label: `${linkLabel(l.name!)} (${l.text || l.url})` }));
  if (trigger.link_name && !linkData.some((d) => d.value === trigger.link_name)) {
    linkData.unshift({ value: trigger.link_name, label: linkLabel(trigger.link_name) });
  }
  return (
    <Stack gap="xs">
      <Select
        size="xs"
        label="In emails made from"
        placeholder="Any email template"
        searchable
        clearable
        data={templates.map((t) => ({ value: t.id, label: t.name }))}
        value={trigger.email_template_id ?? null}
        onChange={(x) => set({ email_template_id: x, link_name: null })}
        data-testid="trigger-template"
      />
      <Select
        size="xs"
        label="Which link"
        placeholder={trigger.email_template_id ? 'Any link' : 'Choose a template to pick one of its named links'}
        clearable
        data={linkData}
        value={trigger.link_name ?? null}
        onChange={(x) => set({ link_name: x })}
        disabled={!trigger.email_template_id && !trigger.link_name}
        nothingFoundMessage="No named links"
        data-testid="trigger-link"
      />
      {trigger.email_template_id && links.length > 0 && named.length === 0 && (
        <Text size="xs" c="dimmed">This template has no named links yet. Name them in the email editor, under Links.</Text>
      )}
      <TextInput
        size="xs"
        label="Or the address contains"
        description="For links without a name. Any case."
        placeholder="calendly.com"
        value={trigger.url_contains ?? ''}
        onChange={(e) => set({ url_contains: e.currentTarget.value || null })}
        data-testid="trigger-url-contains"
      />
      <Text size="sm" fw={500} data-testid="link-sentence">{linkClickSentence(trigger, template?.name)}</Text>
    </Stack>
  );
}

function StreamsSetting({ trigger, set }: { trigger: WorkflowTrigger; set: (p: Partial<WorkflowTrigger>) => void }) {
  const v = useVocab();
  const campaigns = useCampaigns().data?.campaigns ?? [];
  return (
    <MultiSelect
      size="xs"
      label={`Only for these ${v.many('campaign')}`}
      description={`Left empty, every live ${v.one('lead')} starts it. Imports and redistributes never do.`}
      placeholder={`Every ${v.one('campaign')}`}
      searchable
      clearable
      data={campaigns.filter((c) => !c.is_reference_list).map((c) => ({ value: c.id, label: c.name }))}
      value={trigger.campaign_ids ?? []}
      onChange={(x) => set({ campaign_ids: x.length ? x : null })}
      data-testid="trigger-streams"
    />
  );
}

function PropertySetting({ trigger, set }: { trigger: WorkflowTrigger; set: (p: Partial<WorkflowTrigger>) => void }) {
  const v = useVocab();
  const defs = useProperties('person').data?.properties ?? [];
  return (
    <Select
      size="xs"
      label={`Which ${v.one('property')}`}
      placeholder={`Any ${v.one('property')}`}
      searchable
      clearable
      data={defs.map((d) => ({ value: d.key, label: d.label }))}
      value={trigger.property_key ?? null}
      onChange={(x) => set({ property_key: x })}
      data-testid="trigger-property"
    />
  );
}

function DealSetting({ trigger, set }: { trigger: WorkflowTrigger; set: (p: Partial<WorkflowTrigger>) => void }) {
  const v = useVocab();
  const pipelines = (usePipelines().data?.pipelines ?? []).filter((p) => !p.archived);
  const pipeline = pipelines.find((p) => p.id === trigger.pipeline_id) ?? null;
  const stageData = (pipeline ? [pipeline] : pipelines).map((p) => ({
    group: p.name,
    items: p.stages.filter((s) => !s.archived).map((s) => ({ value: s.id, label: s.name })),
  }));
  return (
    <Stack gap="xs">
      <Select
        size="xs"
        label={v.One('pipeline')}
        placeholder={`Any ${v.one('pipeline')}`}
        clearable
        data={pipelines.map((p) => ({ value: p.id, label: p.name }))}
        value={trigger.pipeline_id ?? null}
        onChange={(x) => set({ pipeline_id: x, from_stage_id: null, to_stage_id: null })}
        data-testid="trigger-pipeline"
      />
      {trigger.kind === 'deal_stage_changed' && (
        <Group gap="xs" grow wrap="wrap" align="flex-start">
          <Select
            size="xs"
            label={`From ${v.one('stage')}`}
            placeholder="Any"
            clearable
            searchable
            data={stageData}
            value={trigger.from_stage_id ?? null}
            onChange={(x) => set({ from_stage_id: x })}
            style={{ minWidth: 160 }}
          />
          <Select
            size="xs"
            label={`To ${v.one('stage')}`}
            placeholder="Any"
            clearable
            searchable
            data={stageData}
            value={trigger.to_stage_id ?? null}
            onChange={(x) => set({ to_stage_id: x })}
            style={{ minWidth: 160 }}
            data-testid="trigger-to-stage"
          />
        </Group>
      )}
    </Stack>
  );
}

function BandSetting({ trigger, set }: { trigger: WorkflowTrigger; set: (p: Partial<WorkflowTrigger>) => void }) {
  const bands = useScoringSettings().data?.bands ?? [];
  return (
    <Select
      size="xs"
      label="Into which band"
      placeholder="Any band"
      clearable
      data={bands.map((b) => ({ value: b.key, label: b.label }))}
      value={trigger.band ?? null}
      onChange={(x) => set({ band: x })}
      data-testid="trigger-band"
    />
  );
}
